import type { ReactNode } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { hubPath, stripHubPath } from '../lib/hubPath'
import { pubImg } from '../lib/pubImg'
import { useHubContext } from './HubLayout'

const FALLBACK_LOGO = pubImg('images/eleitorweb-300.png')

const ROOT_PATHS = new Set(['/', '/inicio'])
const SECTION_PATHS = new Set(['/rede', '/campanha', '/perfil', '/convidar', '/medalhas'])

type Props = {
  title: ReactNode
  eyebrow?: ReactNode
  subtitle?: ReactNode
  showCampanha?: boolean
  backTo?: string
  backLabel?: string
  /** Força ocultar o Voltar (ex.: tela raiz). */
  hideBack?: boolean
  showLogo?: boolean
}

function inferBack(pathnameRaw: string): { to: string; label: string } | null {
  const pathname = stripHubPath(pathnameRaw)
  if (ROOT_PATHS.has(pathname)) return null
  if (SECTION_PATHS.has(pathname)) return { to: hubPath('/inicio'), label: 'Início' }

  if (
    ['/perfil', '/cadastro', '/senha', '/ficha'].some((p) => pathname === p || pathname.startsWith(`${p}/`)) ||
    pathname.startsWith('/cadastro') ||
    pathname.startsWith('/senha') ||
    pathname.startsWith('/ficha')
  ) {
    if (pathname !== '/perfil') return { to: hubPath('/perfil'), label: 'Perfil' }
  }

  if (
    ['/rede', '/equipe', '/convidados', '/medalhas', '/ranking', '/pontuacao'].includes(pathname) ||
    pathname.startsWith('/arvore') ||
    pathname.startsWith('/solicitacoes')
  ) {
    if (pathname !== '/rede') return { to: hubPath('/rede'), label: 'Rede' }
  }

  if (
    pathname === '/campanha' ||
    [
      '/missoes',
      '/biografia',
      '/eu-apoio',
      '/colinha',
      '/agenda',
      '/noticias',
      '/videos',
      '/avisos',
      '/eventos',
      '/enquetes',
      '/legislacao',
      '/redes',
    ].some((p) => pathname === p || pathname.startsWith(`${p}/`))
  ) {
    if (pathname !== '/campanha') return { to: hubPath('/campanha'), label: 'Campanha' }
  }

  return { to: hubPath('/inicio'), label: 'Início' }
}

function resolveBackTo(backTo: string): string {
  if (backTo.startsWith('/hub') || backTo.startsWith('/convite')) return backTo
  return hubPath(backTo)
}

export function HubHeader({
  title,
  eyebrow,
  subtitle,
  showCampanha = false,
  backTo,
  backLabel = 'Voltar',
  hideBack = false,
  showLogo = false,
}: Props) {
  const { hub } = useHubContext()
  const location = useLocation()
  const navigate = useNavigate()
  const campanha = hub?.campanha
  const resolvedSub =
    subtitle !== undefined ? subtitle : showCampanha && campanha?.nome ? campanha.nome : null
  const logoSrc = campanha?.logo?.trim() || FALLBACK_LOGO

  const inferred = hideBack
    ? null
    : backTo
      ? { to: resolveBackTo(backTo), label: backLabel }
      : inferBack(location.pathname)
  const pathRel = stripHubPath(location.pathname)
  const canHistoryBack = !hideBack && !inferred && !ROOT_PATHS.has(pathRel)

  return (
    <header className="bp-hub-header">
      {inferred ? (
        <p className="bp-hub-header__back">
          <Link className="bp-hub-header__back-btn" to={inferred.to}>
            ← {inferred.label === 'Voltar' ? 'Voltar' : `Voltar · ${inferred.label}`}
          </Link>
        </p>
      ) : canHistoryBack ? (
        <p className="bp-hub-header__back">
          <button type="button" className="bp-hub-header__back-btn" onClick={() => navigate(-1)}>
            ← Voltar
          </button>
        </p>
      ) : null}

      <div className="bp-hub-header__row">
        <div className="bp-hub-header__text">
          {eyebrow ? <p className="bp-muted bp-hub-header__eyebrow">{eyebrow}</p> : null}
          <h1 className="bp-hub-header__title">{title}</h1>
          {resolvedSub ? <p className="bp-muted bp-hub-header__sub">{resolvedSub}</p> : null}
        </div>

        {showLogo ? (
          <img
            className="bp-hub-header__logo"
            src={logoSrc}
            alt={campanha?.nome || 'Campanha'}
            onError={(e) => {
              if (e.currentTarget.src !== FALLBACK_LOGO) {
                e.currentTarget.src = FALLBACK_LOGO
              }
            }}
          />
        ) : null}
      </div>
    </header>
  )
}
